import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import EntityName from "legal-entities/entity-names/entity-name";
import UserOpenEntityAgreementSchema, {
  UserOpenEntityAgreementJSON,
  UserOpenEntityAgreementParams,
} from "legal-entities/agreements/open/_schema/user-open-entity-agreement-schema";
import Entity from "_framework/entities/entity";
import Address from "_shared/addresses/address";

export default class UserOpenEntityAgreement extends Entity<
  UserOpenEntityAgreement,
  UserOpenEntityAgreementJSON,
  UserOpenEntityAgreementParams
> {
  public static get TEST_AGREEMENT(): UserOpenEntityAgreement {
    return new UserOpenEntityAgreement({
      id: Guid.generate(),
      entityLocation: Address.EXAMPLE,
      entityName: EntityName.create("Test User Open Entity Agreement"),
    });
  }

  public static create(values: UserOpenEntityAgreementParams): UserOpenEntityAgreement {
    const parsed = UserOpenEntityAgreementSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid UserOpenEntityAgreement parameters.");
    }
    return new UserOpenEntityAgreement(parsed);
  }

  public static fromJSON(json: UserOpenEntityAgreementJSON): UserOpenEntityAgreement {
    if (isNullOrUndefined(json)) return json;
    const params = UserOpenEntityAgreementSchema.deserialize(json);
    return new UserOpenEntityAgreement(params);
  }

  public static fromJSONArray(
    jsonArray: UserOpenEntityAgreementJSON[] | null | undefined,
  ): UserOpenEntityAgreement[] | null | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    return jsonArray.map((json) => UserOpenEntityAgreement.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly entityLocation?: Address;
  public readonly entityName: EntityName;
  public readonly onboardingTemplateId?: string;
  public readonly onboardingTemplateVersion?: string;
  public readonly workflowVersion?: number;

  public constructor(params: UserOpenEntityAgreementParams) {
    const validatedParams = UserOpenEntityAgreementSchema.validate(params);
    super(validatedParams);
    this.id = validatedParams.id;
    this.entityName = validatedParams.entityName;
    this.entityLocation = validatedParams.entityLocation;
    this.onboardingTemplateId = validatedParams.onboardingTemplateId;
    this.onboardingTemplateVersion = validatedParams.onboardingTemplateVersion;
    this.workflowVersion = validatedParams.workflowVersion;
  }

  public toJSON(): UserOpenEntityAgreementJSON {
    return UserOpenEntityAgreementSchema.serialize(this);
  }

  public valueOf(): UserOpenEntityAgreementJSON {
    return this.toJSON();
  }

  public isEqualTo(other: UserOpenEntityAgreement | null | undefined): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toString(): string {
    return `UserOpenEntityAgreement (
      id: ${this.id.value}
      entityName: ${this.entityName.toString()}
    )`;
  }
}
